import React, { ReactElement, useEffect, useState, type JSX } from 'react';
import { Button, Spin } from 'antd';
import useArticle, { Article } from 'vl-common/src/hooks/useArticle';
import { ConfirmationModal } from 'vl-common/src/components/Modals';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import CookieList from './CookieList';

interface CookieSettingsProps {
  acceptCookies: (cookies?: string[]) => void;
  setSettingsOpen: (open: boolean) => void;
  setBannerOpen: (open: boolean) => void;
  article?: Article;
}

const CookieSettings = ({
  acceptCookies,
  setSettingsOpen,
  setBannerOpen,
  article
}: CookieSettingsProps): JSX.Element => {
  const { t } = useTranslation();
  const essentialCookies = useArticle('cookies_essential', true);
  const functionalCookies = useArticle('cookies_functional', true);
  const performanceCookies = useArticle('cookies_performance', true);
  const cookiesSettingLoaded = Boolean(
    essentialCookies?.body && functionalCookies?.body && performanceCookies?.body && article
  );

  const [cookies, setCookies] = useState<
    Record<
      string,
      {
        accepted: boolean;
        editable?: boolean;
        description: ReactElement;
        title: string;
      }
    >
  >({});

  useEffect(() => {
    setCookies({
      essential: {
        accepted: true,
        editable: false,
        description: (
          <div className="accessibility-statement" dangerouslySetInnerHTML={{ __html: essentialCookies?.body || '' }} />
        ),
        title: t('Essential / Necessary')
      },
      performance: {
        accepted: !!(
          localStorage.getItem('cookie-consent')?.includes('all') ||
          localStorage.getItem('cookie-consent')?.includes('per')
        ),
        editable: true,
        description: (
          <div
            className="accessibility-statement"
            dangerouslySetInnerHTML={{ __html: performanceCookies?.body || '' }}
          />
        ),
        title: t('Performance')
      },
      functional: {
        accepted: !!(
          localStorage.getItem('cookie-consent')?.includes('all') ||
          localStorage.getItem('cookie-consent')?.includes('fun')
        ),
        editable: true,
        description: (
          <div
            className="accessibility-statement"
            dangerouslySetInnerHTML={{ __html: functionalCookies?.body || '' }}
          />
        ),
        title: t('Functional')
      }
    });
  }, [cookiesSettingLoaded]);

  const acceptAllCookies = (): void => {
    acceptCookies();
  };

  const updateCookieSettings = () => {
    acceptCookies(Object.keys(cookies).filter((cookie) => cookies[cookie].accepted));
  };

  return (
    <ConfirmationModal
      title="Privacy Preference Center"
      open
      onCancel={() => {
        setSettingsOpen(false);
        setBannerOpen(true);
      }}
      footer={[
        <Button
          key="close"
          onClick={() => {
            setSettingsOpen(false);
            setBannerOpen(true);
          }}
          data-testid="close"
        >
          {t('Close')}
        </Button>,
        <Button key="update" onClick={updateCookieSettings} data-testid="confirm-selection">
          {t('Confirm choices')}
        </Button>,
        <Button key="accept" onClick={acceptAllCookies} type="primary" data-testid="accept-all">
          {t('Accept all cookies')}
        </Button>
      ]}
    >
      {!cookiesSettingLoaded ? (
        <Spin
          style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
          indicator={<LoadingOutlined style={{ fontSize: 22 }} spin />}
        />
      ) : (
        <div>
          {article && <div className="accessibility-statement" dangerouslySetInnerHTML={{ __html: article.body }} />}
          <a
            rel="noreferrer"
            target="_blank"
            href="/patients/legal/?all_policies=0&activeTab=COOKIES"
            tabIndex={0}
            data-testid="cookie_notice"
          >
            {t('Cookie Notice')}
          </a>

          <CookieList cookies={cookies} setCookies={setCookies} />
        </div>
      )}
    </ConfirmationModal>
  );
};

export default CookieSettings;
